import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Avatar from "../common/Avatar";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import MessageRequestCard, { formatRequestAge } from "./MessageRequestCard";
import ConfirmationModal from "../club/ConfirmationModal";
import { TabButton } from "./InboxTabs";
import messageRequestService, {
  MESSAGE_REQUEST_INTENTS,
  MESSAGE_REQUEST_INTENT_BY_KEY,
  MESSAGE_REQUEST_ORIGINS,
  MESSAGE_REQUEST_ORIGIN_BY_KEY,
} from "../../services/messageRequestApi";

// How many pending requests render as full cards before the rest collapse into
// the compact "OLDER" list (design 1f shows 3 full cards, then OLDER rows).
const RECENT_LIMIT = 3;

// Compact row for the Older section (design 1f): 32px avatar, "Name · Agency",
// an intent/origin summary line, date, and small Accept / Ignore controls.
const OlderRow = ({ request, onSelect, onAccept, onIgnore, mobile = false, t }) => {
  const sender = request.sender || {};
  const name = sender.name || t("Agent");
  const agency = sender.agency || "";
  const intent = MESSAGE_REQUEST_INTENT_BY_KEY[request.intent];
  const origin = MESSAGE_REQUEST_ORIGIN_BY_KEY[request.origin];
  // Manual intents carry the sender's own words on the summary line
  // (design 1f: `Manual: "Youth tournament invite" · Direct`).
  const manualText = request.intent === "manual" ? request.intent_manual_text : "";
  const intentLabel = manualText
    ? `${intent?.filterLabel || t("Manual")}: "${manualText}"`
    : intent?.filterLabel;
  const summary = [intentLabel, origin?.tagLabel].filter(Boolean).join(" · ");
  const age = formatRequestAge(request.created_at);

  // Mobile older row (design 1t): 36px avatar, name only, the summary + date
  // combined on one line, a disclosure chevron — no inline Accept/Ignore.
  if (mobile) {
    return (
      <div
        className="flex cursor-pointer items-center gap-2.5"
        style={{ padding: "2px 4px" }}
        onClick={() => onSelect?.(request)}
      >
        <Avatar src={sender.profile_picture_url} alt={name} fallbackText={name} size={36} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[13px] font-semibold text-[#475467]">{name}</span>
          <span className="truncate text-[11px] text-[#98A2B3]">
            {[summary, age].filter(Boolean).join(" · ")}
          </span>
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0">
          <path
            d="M4 2.5L8 6L4 9.5"
            stroke="#98A2B3"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className="flex cursor-pointer items-center gap-2.5"
      style={{ padding: "7px 4px" }}
      onClick={() => onSelect?.(request)}
    >
      <Avatar src={sender.profile_picture_url} alt={name} fallbackText={name} size={32} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[12.5px] font-semibold text-[#475467]">
          {name}
          {agency && <span className="font-normal text-[#98A2B3]"> · {agency}</span>}
        </span>
        <span className="truncate text-[11px] text-[#98A2B3]">{summary}</span>
      </div>
      <span className="flex-shrink-0 text-[11px] text-[#98A2B3]">{age}</span>
      <div className="flex flex-shrink-0 gap-1.5">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAccept?.(request.id);
          }}
          className="rounded-[7px] text-[11px] font-semibold text-white"
          style={{ padding: "4px 12px", background: "#2283FF" }}
        >
          {t("Accept")}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIgnore?.(request.id);
          }}
          className="rounded-[7px] text-[11px] font-semibold text-[#475467]"
          style={{ padding: "4px 12px", background: "#FFFFFF", border: "1px solid #E4E9F0" }}
        >
          {t("Ignore")}
        </button>
      </div>
    </div>
  );
};

// The green "Accept moment" banner (design 1g). Shown once at the top of the list
// after a request is accepted; the accepted card silently leaves the list.
const AcceptedBanner = ({ banner, onOpen, t }) => (
  <div
    className="flex items-center gap-2.5 rounded-[12px]"
    style={{ padding: "14px 13px", background: "#F2FBF5", border: "1px solid #B7E2C6" }}
  >
    <div
      className="flex flex-shrink-0 items-center justify-center rounded-full"
      style={{ width: 34, height: 34, background: "#1B9E52" }}
    >
      <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
        <path
          d="M3 7.5L5.8 10L11 4.5"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
    <div className="flex flex-1 flex-col gap-0.5">
      <span className="text-[13px] font-bold text-[#17803D]">
        {t("Accepted: conversation is live")}
      </span>
      <span className="text-[11.5px] text-[#3D8256]">
        {banner.name}
        {" → "}
        {t("your")} <span className="font-bold">{t("Last Minute")}</span>{" "}
        {t("tab · 14-day timer started, you hold it")}
      </span>
    </div>
    <button
      type="button"
      onClick={() => onOpen?.(banner.channel_cid)}
      className="flex-shrink-0 text-[12px] font-semibold text-[#17803D]"
    >
      {t("Open →")}
    </button>
  </div>
);

// MessageRequestsPanel — the pending "Requests" gate list (design frames 1f + 1g
// + 1h). Self-fetching + self-managing: intent filter (client-side, so the chip
// counts stay in the current ORIGIN scope), origin segmented control
// (server-side), the Older section, the empty state, an optimistic Ignore
// (the card silently leaves, zero other UI reaction — design 1g) and the Accept
// transition banner.
//
// Props (all optional — the panel runs standalone):
//   selectedId      id of the request whose detail pane is open (1f two-pane).
//   onSelect        (request) => void  — open detail pane.
//   onOpenThread    (channel_cid) => void — deep-link from the Accept banner /
//                                          toast into the freshly created chat.
//   onAccepted      (request, channel_cid) => void — side effects (toast, tab
//                                          badge refresh) live with the integrator.
//   onCountsChange  (total) => void — feed the "Requests" tab unread badge.
//   t               i18n passthrough (identity default = verbatim copy).
//
// DESIGN_006 — the SENDER side lives here too, as a second view behind a
// Received / Sent switch (D16: underline = navigation, pill = filter/status, so
// the switch is underline tabs and NOT another pill row next to the intent
// chips). The artboard fixes the entry point — frame 1r's link "View your N
// pending requests" — but not the placement of the list it opens, so it reuses
// this surface rather than inventing a route.
//
// The Sent view is deliberately ACCOUNT-WIDE, not lane-scoped like Received:
// its N must be the same N frame 1r promises, and that number is the D12 cap
// count (`cooldown-status.open_count`), which spans every origin. Each sent card
// still carries its origin tag, so the lane a request came from stays legible.
//   defaultView  "received" | "sent" — which view opens first (the 1r link
//                deep-links straight to "sent").
const MessageRequestsPanel = ({
  selectedId = null,
  onSelect,
  onOpenThread,
  onAccepted,
  onCountsChange,
  onProvideActions,
  defaultView = "received",
  // D29: when the gate is mounted inside a lane, the lane IS the origin filter —
  // the panel fetches only this origin's requests and HIDES the ORIGIN segmented
  // control. null = the legacy standalone panel (origin control visible).
  laneOrigin = null,
  // D29: embedded as a gate ABOVE a lane's conversation list. When there is
  // nothing pending it collapses to null (the lane shows only conversations)
  // instead of the full-height empty illustration.
  embedded = false,
  t = (s) => s,
}) => {
  const [origin, setOrigin] = useState(laneOrigin || "all");
  const [intent, setIntent] = useState("all");
  const [items, setItems] = useState([]); // origin-scoped, unfiltered by intent
  const [loading, setLoading] = useState(true);
  // True after the FIRST completed fetch. The filter bar (intent chips +
  // ORIGIN control) must not paint during the initial load: it would show a
  // bogus "All 0" and, for users with zero requests, flash for ~0.5s and
  // vanish again (Marin's ScreenClubReq report). Once loaded it stays mounted
  // through origin-switch refetches so the control never disappears mid-use.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null); // { name, channel_cid }
  const [olderOpen, setOlderOpen] = useState(true);

  // ── DESIGN_006 sender side ────────────────────────────────────────────────
  const [view, setView] = useState(defaultView === "sent" ? "sent" : "received");
  // Account-wide OPEN outbox. The backend already drops withdrawn/expired rows
  // and collapses ignored to 'pending' (Invariant 9), so filtering to 'pending'
  // here just removes the accepted ones (they have graduated to real chats).
  const [sentItems, setSentItems] = useState([]);
  const [sentLoaded, setSentLoaded] = useState(false);
  // The request awaiting the destructive-confirm dialog (the app's ChatActionModal
  // pattern), and the id currently being withdrawn.
  const [confirmWithdraw, setConfirmWithdraw] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  // A deep link (?requests=sent) may arrive after mount — follow it.
  useEffect(() => {
    if (defaultView === "sent") setView("sent");
  }, [defaultView]);

  // Mobile (@402, design 1t) scales the cards up and simplifies the Older rows.
  const isMobile = useMediaQuery("(max-width: 767px)");

  // The integrator hands `onCountsChange` in as an INLINE arrow function
  // (Inbox.js re-creates it on every render). `load` used to list it as a
  // useCallback dependency, which closed a loop: fetch completes →
  // onCountsChange → setPendingRequestsByLane spreads a fresh object →
  // Inbox re-renders → new callback identity → new `load` → the effect
  // below re-fires → fetch again, forever. The visible symptom was the
  // list spinner between the filter bar ("All 0") and the thread list
  // never stopping (Marin, staging mobile) while the endpoint was being
  // hammered. The callback lives in a ref now, so the fetch lifecycle
  // depends on `origin` alone and runs exactly once per origin.
  const onCountsChangeRef = useRef(onCountsChange);
  useEffect(() => {
    onCountsChangeRef.current = onCountsChange;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await messageRequestService.listReceived({ origin });
    if (res.success) {
      setItems(res.results);
      onCountsChangeRef.current?.(res.results.length);
    } else if (res.status === 403 || res.status === 401) {
      // Viewer isn't allowed to receive message requests (e.g. a club account —
      // requests are agent↔agent). Treat as an empty gate, never a red error
      // banner. The mount is already agent-gated in Inbox.js; this is the
      // defensive belt-and-braces so a stray 403 can't paint an error (QA S2).
      setItems([]);
      onCountsChangeRef.current?.(0);
    } else {
      setError(res.error);
    }
    setLoading(false);
    setHasLoadedOnce(true);
  }, [origin]);

  useEffect(() => {
    load();
  }, [load]);

  // D29: keep the fetched origin pinned to the hosting lane.
  useEffect(() => {
    if (laneOrigin) setOrigin(laneOrigin);
  }, [laneOrigin]);

  // The sender's open outbox. Fetched once alongside the received list (never
  // per row — the "Request pending" state on profiles/search rows reads the same
  // single payload), so the Sent tab can carry its count before it is opened and
  // the gate knows whether it has anything at all to show.
  const loadSent = useCallback(async () => {
    const res = await messageRequestService.listSent();
    if (res.success) {
      setSentItems((res.results || []).filter((r) => r.status !== "accepted"));
    } else if (res.status === 403 || res.status === 401) {
      setSentItems([]); // non-agent viewer — silent empty, never a red banner
    }
    setSentLoaded(true);
  }, []);

  useEffect(() => {
    loadSent();
  }, [loadSent]);

  // Per-intent counts within the current origin scope (design 1f chips).
  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const it of MESSAGE_REQUEST_INTENTS) c[it.key] = 0;
    for (const r of items) {
      if (c[r.intent] != null) c[r.intent] += 1;
    }
    return c;
  }, [items]);

  const filtered = useMemo(
    () => (intent === "all" ? items : items.filter((r) => r.intent === intent)),
    [items, intent],
  );

  // The backend stamps each row with `is_older` (>30d, confirmed P2-BE-1) — the
  // authoritative signal for the OLDER section (design 1f). Fall back to a simple
  // "first N are recent" heuristic only if the flag isn't present.
  const hasOlderFlag = filtered.some((r) => typeof r.is_older === "boolean");
  const recent = hasOlderFlag
    ? filtered.filter((r) => !r.is_older)
    : filtered.slice(0, RECENT_LIMIT);
  const older = hasOlderFlag
    ? filtered.filter((r) => r.is_older)
    : filtered.slice(RECENT_LIMIT);

  // Optimistic ignore: remove immediately, zero other UI reaction (design 1g).
  const handleIgnore = useCallback(
    async (id) => {
      const snapshot = items;
      const next = items.filter((r) => r.id !== id);
      setItems(next);
      onCountsChangeRef.current?.(next.length);
      const res = await messageRequestService.ignoreRequest(id);
      if (!res.success) {
        setItems(snapshot); // restore on failure
        onCountsChangeRef.current?.(snapshot.length);
      }
    },
    [items],
  );

  const handleAccept = useCallback(
    async (id) => {
      const req = items.find((r) => r.id === id);
      const res = await messageRequestService.acceptRequest(id);
      if (res.success) {
        const next = items.filter((r) => r.id !== id);
        setItems(next);
        onCountsChangeRef.current?.(next.length);
        setBanner({
          name: req?.sender?.name || t("Agent"),
          channel_cid: res.channel_cid,
        });
        onAccepted?.(req, res.channel_cid);
      }
      return res;
    },
    [items, onAccepted, t],
  );

  // Withdraw (DESIGN_006 §3). The row leaves the list and the D12 cap slot is
  // freed. Idempotent-safe: a repeat call is a harmless no-op server-side.
  // 409 = the recipient accepted in the meantime — a chat now exists, so the row
  // is refreshed into its accepted state (which drops it out of the OPEN outbox)
  // instead of being removed as "withdrawn". Never surfaces a rejection wording.
  const handleWithdraw = useCallback(async (id) => {
    setWithdrawing(true);
    const res = await messageRequestService.withdrawRequest(id);
    setWithdrawing(false);
    setConfirmWithdraw(null);
    if (res.success || res.status === 409) {
      // Both outcomes remove the row from the OPEN outbox: withdrawn frees a
      // slot, accepted graduated to a chat. Re-fetch so the count the frame-1r
      // link shows stays exactly the server's open_count.
      setSentItems((prev) => prev.filter((r) => r.id !== id));
      loadSent();
      return res;
    }
    setError(res.error);
    return res;
  }, [loadSent]);

  // Expose the accept/ignore flows to the integrator (the two-pane 1f detail
  // pane invokes them, so the list stays the single owner of the optimistic
  // mutation + the "Accepted" banner). Additive — no-op when the prop is absent.
  useEffect(() => {
    onProvideActions?.({ accept: handleAccept, ignore: handleIgnore });
  }, [onProvideActions, handleAccept, handleIgnore]);

  const sentView = view === "sent";

  // Genuinely-empty inbox (no requests in any origin): the empty state (1h)
  // shows the tabs then the illustration — NOT the intent/origin filter bar.
  const noRequests = origin === "all" && items.length === 0;
  // Never during the initial load (no flash, no bogus "All 0"); afterwards for
  // every non-genuinely-empty state, including origin-scoped refetches (the
  // control must not vanish while the user is switching origins) and errors.
  // The intent/origin filters describe the RECEIVED list only.
  const showFilters = !sentView && hasLoadedOnce && (error || !noRequests);

  // The Received / Sent switch only earns its space once there is something to
  // switch between — otherwise a lane with no requests at all would grow a
  // permanent "Received 0 · Sent 0" strip, which D29's collapse rule forbids.
  const showViewTabs =
    hasLoadedOnce && sentLoaded && (sentItems.length > 0 || sentView);

  // D29 embedded gate: once loaded with nothing pending in EITHER direction,
  // render nothing so the lane is pure conversations (no empty illustration, no
  // filter bar). An open outbox keeps the gate alive so "Sent" stays reachable.
  // The one exception: when the viewer deliberately opened the Sent view (the
  // 1r link), an empty outbox gets its empty state rather than a vanished panel.
  if (
    embedded &&
    hasLoadedOnce &&
    sentLoaded &&
    !error &&
    !banner &&
    !sentView &&
    filtered.length === 0 &&
    sentItems.length === 0
  ) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Received / Sent — D16 underline tabs (navigation), reusing the ONE
          canonical tab button so the gate never grows a second tab style. */}
      {showViewTabs && (
        <div className="flex flex-none gap-[15px] border-b border-[#E4E9F0] px-[14px]">
          <TabButton
            tab={{ key: "received", label: t("Received"), count: items.length }}
            isActive={!sentView}
            onChange={() => setView("received")}
            mobile={isMobile}
          />
          <TabButton
            tab={{ key: "sent", label: t("Sent"), count: sentItems.length }}
            isActive={sentView}
            onChange={() => setView("sent")}
            mobile={isMobile}
          />
        </div>
      )}

      {/* Filters: intent chips (with counts) + origin segmented control (1f). */}
      {showFilters && (
      <div
        className="flex flex-col gap-2 border-b border-[#F0F2F6]"
        style={{ padding: "12px 14px 10px" }}
      >
        <div className="flex flex-wrap gap-1.5">
          <FilterChip
            active={intent === "all"}
            label={`${t("All")} ${counts.all}`}
            activeBg="#101828"
            activeText="#FFFFFF"
            bg="#F2F4F7"
            text="#475467"
            onClick={() => setIntent("all")}
          />
          {MESSAGE_REQUEST_INTENTS.filter((it) => counts[it.key] > 0).map((it) => (
            <FilterChip
              key={it.key}
              active={intent === it.key}
              label={`${it.filterLabel} ${counts[it.key]}`}
              activeBg={it.pillBg}
              activeText={it.pillText}
              bg={it.pillBg}
              text={it.pillText}
              ring={it.pillText}
              onClick={() => setIntent(it.key)}
            />
          ))}
        </div>
        {/* D29: the ORIGIN control only exists on the legacy standalone panel;
            inside a lane the lane IS the origin filter, so it's hidden. */}
        {!laneOrigin && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10.5px] font-semibold text-[#98A2B3]">
            {t("ORIGIN")}
          </span>
          <div
            className="flex rounded-[8px] text-[11px] font-semibold text-[#667085]"
            style={{ background: "#F2F4F7", padding: 2 }}
          >
            {[{ key: "all", filterLabel: t("All") }, ...MESSAGE_REQUEST_ORIGINS].map(
              (o) => {
                const active = origin === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setOrigin(o.key)}
                    className="rounded-[6px]"
                    style={{
                      padding: "4px 10px",
                      background: active ? "#FFFFFF" : "transparent",
                      color: active ? "#101828" : "#667085",
                      boxShadow: active ? "0 1px 2px rgba(16,24,40,.08)" : "none",
                    }}
                  >
                    {o.filterLabel}
                  </button>
                );
              },
            )}
          </div>
        </div>
        )}
      </div>
      )}

      {/* List. `overscroll-y-none` (not just `contain`): this list nests
          inside Inbox's 52%-capped section, and on iOS a nested overflow
          container rubber-bands under the finger even when its content
          FITS — the section felt draggable with 2 entries (Marin,
          mobile-sweep follow-up). `none` kills that independent bounce and
          the scroll chaining; real scrolling (content > max-h) is
          untouched, so the lid behaviour stays. */}
      <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto overscroll-y-none" style={{ padding: "10px 12px" }}>
        {banner && !sentView && (
          <AcceptedBanner banner={banner} onOpen={onOpenThread} t={t} />
        )}

        {sentView ? (
          sentItems.length === 0 ? (
            <SentEmptyState t={t} />
          ) : (
            <>
              {error && (
                <p className="px-1 text-[12.5px] text-[#B42318]">{error}</p>
              )}
              {sentItems.map((r) => (
                <MessageRequestCard
                  key={r.id}
                  request={r}
                  variant="sent"
                  onWithdraw={() => setConfirmWithdraw(r)}
                  mobile={isMobile}
                  t={t}
                />
              ))}
              <div
                className="mt-auto text-[11px] text-[#98A2B3]"
                style={{ padding: "4px 4px 0" }}
              >
                {/* Frame 1r copy, verbatim — the same sentence that explains the
                    cap in the composer explains the list it links to. */}
                {t(
                  "New sends unlock as agents accept — or as you withdraw a pending request.",
                )}
              </div>
            </>
          )
        ) : loading ? (
          <div className="flex justify-center py-6">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#DDE3EC] border-t-[#2283FF]" />
          </div>
        ) : error ? (
          <p className="px-1 py-4 text-[12.5px] text-[#B42318]">{error}</p>
        ) : filtered.length === 0 && !banner ? (
          <EmptyState t={t} />
        ) : (
          <>
            {recent.map((r) => (
              <MessageRequestCard
                key={r.id}
                request={r}
                selected={r.id === selectedId}
                onSelect={onSelect}
                onAccept={handleAccept}
                onIgnore={handleIgnore}
                mobile={isMobile}
                t={t}
              />
            ))}

            {older.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setOlderOpen((v) => !v)}
                  className="flex items-center gap-2.5"
                  style={{ padding: "6px 2px 0" }}
                >
                  <span
                    className="font-semibold uppercase text-[#98A2B3]"
                    style={{
                      fontSize: "9.5px",
                      letterSpacing: ".06em",
                      fontFamily: "ui-monospace, Menlo, monospace",
                    }}
                  >
                    {t("OLDER")} · {older.length}{" "}
                    {older.length === 1 ? t("REQUEST") : t("REQUESTS")}
                  </span>
                  <span className="h-px flex-1 bg-[#E9EDF2]" />
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{
                      transform: olderOpen ? "none" : "rotate(-90deg)",
                      transition: "transform .15s",
                    }}
                  >
                    <path
                      d="M2.5 4L6 8L9.5 4"
                      stroke="#98A2B3"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {olderOpen &&
                  older.map((r) => (
                    <OlderRow
                      key={r.id}
                      request={r}
                      onSelect={onSelect}
                      onAccept={handleAccept}
                      onIgnore={handleIgnore}
                      mobile={isMobile}
                      t={t}
                    />
                  ))}
              </>
            )}

            <div
              className="mt-auto text-[11px] text-[#98A2B3]"
              style={{ padding: "4px 4px 0" }}
            >
              {t("Requests never expire. They wait here until you act.")}
            </div>
          </>
        )}
      </div>

      {/* Destructive confirm — the app's existing Cancel/Confirm modal (the same
          one the club Staff/Tender cards use). The copy stays inside the sender's
          two-word world: the request goes back, nothing is "rejected" and the
          recipient is never told. Why it frees a slot is already on the list
          footer in frame 1r's own words, so the dialog stays a single question. */}
      <ConfirmationModal
        isOpen={!!confirmWithdraw}
        onClose={() => setConfirmWithdraw(null)}
        onConfirm={() => handleWithdraw(confirmWithdraw?.id)}
        variant="v2"
        message={t("Withdraw this request?")}
        confirmText={t("Withdraw")}
        confirmButtonClass="bg-[#2283FF]"
        loading={withdrawing}
      />
    </div>
  );
};

const FilterChip = ({ active, label, activeBg, activeText, bg, text, ring, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full font-semibold"
    style={{
      padding: "4px 10px",
      fontSize: "11px",
      background: active ? activeBg : bg,
      color: active ? activeText : text,
      boxShadow: active && ring ? `inset 0 0 0 1.5px ${ring}` : "none",
    }}
  >
    {label}
  </button>
);

// Empty state (design frame 1h).
const EmptyState = ({ t }) => (
  <div
    className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
    style={{ padding: "0 44px" }}
  >
    <div
      className="flex items-center justify-center rounded-full"
      style={{ width: 64, height: 64, background: "#F0F6FF" }}
    >
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 38, height: 38, background: "#EAF2FF" }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="2.5" y="4" width="11" height="8.5" rx="1.8" stroke="#1466D8" strokeWidth="1.5" />
          <path d="M3 5l5 4 5-4" stroke="#1466D8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
    <span className="text-[14.5px] font-bold text-[#101828]">
      {t("No pending requests")}
    </span>
    <span className="text-[12.5px] leading-[1.55] text-[#667085]">
      {t(
        "Agents who want to reach you appear here with an intent and a first message. Requests wait for you. They never expire on their own.",
      )}
    </span>
  </div>
);

// Sent view with nothing outstanding. Same illustration language as 1h, worded
// from the sender's side and inside the two-word vocabulary (frame 1k).
const SentEmptyState = ({ t }) => (
  <div
    className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
    style={{ padding: "0 44px" }}
  >
    <div
      className="flex items-center justify-center rounded-full"
      style={{ width: 64, height: 64, background: "#F0F6FF" }}
    >
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 38, height: 38, background: "#EAF2FF" }}
      >
        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="5.4" stroke="#1466D8" strokeWidth="1.5" />
          <path d="M7 4.4V7L8.8 8.2" stroke="#1466D8" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
    <span className="text-[14.5px] font-bold text-[#101828]">
      {t("No pending requests")}
    </span>
    <span className="text-[12.5px] leading-[1.55] text-[#667085]">
      {t(
        "Requests you send wait here until the other agent accepts. You can withdraw one at any time.",
      )}
    </span>
  </div>
);

export default MessageRequestsPanel;
